import React, { useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';

import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { createUser } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type UserCreateFormHandle = {
  submit: () => void;
};

type UserCreateFormProps = {
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const UserCreateForm = React.forwardRef<UserCreateFormHandle, UserCreateFormProps>(
  ({ onCreate, onCancel, setProgress }, ref) => {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [mailAddress, setMailAddress] = useState('');
    const [password, setPassword] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!userId) newErrors.userId = MSG_REQUIRED('ユーザーID');
      else if (userId.length > 300) newErrors.userId = MSG_MAX_LENGTH('ユーザーID', 300);

      if (!userName) newErrors.userName = MSG_REQUIRED('ユーザー名');
      else if (userName.length > 300) newErrors.userName = MSG_MAX_LENGTH('ユーザー名', 300);

      if (!mailAddress) newErrors.mailAddress = MSG_REQUIRED('メールアドレス');
      else if (mailAddress.length > 300)
        newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);

      if (!password) newErrors.password = MSG_REQUIRED('パスワード');
      else if (password.length > 300) newErrors.password = MSG_MAX_LENGTH('パスワード', 300);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createUser({ userId, userName, mailAddress, password })
        .then(() => {
          successDefault('ユーザーを作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('ユーザーの作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={STICKY_HEADER_SX}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={onCancel}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>
                ユーザー登録
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            新しいユーザーアカウントを作成します
          </Typography>
        </Box>

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
          <TextField
            label="ユーザーID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!errors.userId}
            helperText={errors.userId}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="ユーザー名"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!errors.userName}
            helperText={errors.userName}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="メールアドレス"
            value={mailAddress}
            onChange={(e) => setMailAddress(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!errors.mailAddress}
            helperText={errors.mailAddress}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            label="パスワード"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!errors.password}
            helperText={errors.password}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>
      </Stack>
    );
  },
);

UserCreateForm.displayName = 'UserCreateForm';

export default UserCreateForm;
